import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  Grid,
  IconButton,
  InputAdornment,
  Pagination
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { BRANDIDENTITY } from 'utils/rest';
import { useTheme } from 'contexts/ThemeContext';

const SocialNetworkContainer = () => {
  const { themeColors } = useTheme();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userAccessTokenInputs, setUserAccessTokenInputs] = useState({});
  const [pageAccessTokenInputs, setPageAccessTokenInputs] = useState({});
  const [validatingTokens, setValidatingTokens] = useState({});
  const [successMessages, setSuccessMessages] = useState({});
  const [showUserAccessTokens, setShowUserAccessTokens] = useState({});
  const [showPageAccessTokens, setShowPageAccessTokens] = useState({});
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    loadBrandIdentities();
    // eslint-disable-next-line
  }, [page]);

  const loadBrandIdentities = async () => {
    setLoading(true);
    try {
      const {
        findBrandIdentity,
        totalItems
      } = await BRANDIDENTITY.getAllUserBrandIdentity(page, '');
      setTotalItems(totalItems);

      if (findBrandIdentity) {
        const formattedBrands = findBrandIdentity.map(elm => ({
          id: elm?.groupId,
          name: elm?.name[0],
          userAccessToken: elm?.socialNetworkToken?.userAccessToken || '',
          pageAccessToken: elm?.socialNetworkToken?.pageAccessToken || ''
        }));

        setBrands(formattedBrands);

        const initialUserAccessTokens = {};
        const initialPageAccessTokens = {};
        const initialShowUserAccessTokens = {};
        const initialShowPageAccessTokens = {};

        formattedBrands.forEach(brand => {
          initialUserAccessTokens[brand?.id] = brand?.userAccessToken || '';
          initialPageAccessTokens[brand?.id] = brand?.pageAccessToken || '';
          initialShowUserAccessTokens[brand?.id] = false;
          initialShowPageAccessTokens[brand?.id] = false;
        });

        setUserAccessTokenInputs(initialUserAccessTokens);
        setPageAccessTokenInputs(initialPageAccessTokens);
        setShowUserAccessTokens(initialShowUserAccessTokens);
        setShowPageAccessTokens(initialShowPageAccessTokens);
      }
    } catch (error) {
      console.error(
        'Erreur lors du chargement des identités de marque:',
        error
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUserAccessTokenChange = (brandId, value) => {
    setUserAccessTokenInputs(prev => ({
      ...prev,
      [brandId]: value
    }));
    if (successMessages[brandId]) {
      setSuccessMessages(prev => {
        const newMessages = { ...prev };
        delete newMessages[brandId];
        return newMessages;
      });
    }
  };

  const handlePageAccessTokenChange = (brandId, value) => {
    setPageAccessTokenInputs(prev => ({
      ...prev,
      [brandId]: value
    }));
    if (successMessages[brandId]) {
      setSuccessMessages(prev => {
        const newMessages = { ...prev };
        delete newMessages[brandId];
        return newMessages;
      });
    }
  };

  const toggleUserAccessTokenVisibility = brandId => {
    setShowUserAccessTokens(prev => ({
      ...prev,
      [brandId]: !prev[brandId]
    }));
  };

  const togglePageAccessTokenVisibility = brandId => {
    setShowPageAccessTokens(prev => ({
      ...prev,
      [brandId]: !prev[brandId]
    }));
  };

  const validateAndSaveTokens = async brandId => {
    const userAccessToken = userAccessTokenInputs[brandId];
    const pageAccessToken = pageAccessTokenInputs[brandId];

    if (!userAccessToken.trim() || !pageAccessToken.trim()) {
      console.error('Veuillez entrer les deux tokens');
      return;
    }

    setValidatingTokens(prev => ({
      ...prev,
      [brandId]: true
    }));

    try {
      const response = await BRANDIDENTITY.updateBrandIdentity(brandId, {
        socialNetworkToken: {
          userAccessToken,
          pageAccessToken
        }
      });

      if (response) {
        setSuccessMessages(prev => ({
          ...prev,
          [brandId]: true
        }));

        setTimeout(() => {
          setSuccessMessages(prev => {
            const newMessages = { ...prev };
            delete newMessages[brandId];
            return newMessages;
          });
        }, 3000);

        setBrands(prev =>
          prev.map(brand =>
            brand.id === brandId
              ? {
                  ...brand,
                  userAccessToken: userAccessToken,
                  pageAccessToken: pageAccessToken
                }
              : brand
          )
        );
      } else {
        console.error(
          'Erreur lors de la validation des tokens: ' +
            (response.message || 'Erreur inconnue')
        );
      }
    } catch (error) {
      console.error('Erreur lors de la validation des tokens:', error);
      alert('Erreur lors de la validation des tokens');
    } finally {
      setValidatingTokens(prev => ({
        ...prev,
        [brandId]: false
      }));
    }
  };

  const isButtonDisabled = brandId => {
    return (
      !userAccessTokenInputs[brandId]?.trim() ||
      !pageAccessTokenInputs[brandId]?.trim() ||
      validatingTokens[brandId]
    );
  };
  const handleChange = (event, value) => {
    setPage(value);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 'bold' }}>
        Gestion des Tokens d'Accès
      </Typography>

      {brands?.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">
            Aucune identité de marque trouvée
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {brands?.map(brand => (
            <Card key={brand.id} sx={{ boxShadow: 1 }}>
              <CardContent>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} sm={12} md={2}>
                    <Box>
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        {brand.name}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3.5}>
                    <TextField
                      fullWidth
                      type={
                        showUserAccessTokens[brand.id] ? 'text' : 'password'
                      }
                      label="User Access Token"
                      placeholder="Entrez le token utilisateur"
                      value={userAccessTokenInputs[brand.id] || ''}
                      onChange={e =>
                        handleUserAccessTokenChange(brand.id, e.target.value)
                      }
                      size="small"
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                toggleUserAccessTokenVisibility(brand.id)
                              }
                              edge="end"
                              size="small"
                            >
                              {showUserAccessTokens[brand.id] ? (
                                <VisibilityIcon fontSize="small" />
                              ) : (
                                <VisibilityOffIcon fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3.5}>
                    <TextField
                      fullWidth
                      type={
                        showPageAccessTokens[brand.id] ? 'text' : 'password'
                      }
                      label="Page Access Token"
                      placeholder="Entrez le token page"
                      value={pageAccessTokenInputs[brand.id] || ''}
                      onChange={e =>
                        handlePageAccessTokenChange(brand.id, e.target.value)
                      }
                      size="small"
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                togglePageAccessTokenVisibility(brand.id)
                              }
                              edge="end"
                              size="small"
                            >
                              {showPageAccessTokens[brand.id] ? (
                                <VisibilityIcon fontSize="small" />
                              ) : (
                                <VisibilityOffIcon fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={12} md={2}>
                    <Button
                      fullWidth
                      onClick={() => validateAndSaveTokens(brand.id)}
                      disabled={isButtonDisabled(brand.id)}
                      variant="contained"
                      color={themeColors.dark.content}
                    >
                      {validatingTokens[brand.id] ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Validation...
                        </>
                      ) : (
                        'Valider'
                      )}
                    </Button>
                  </Grid>

                  <Grid item xs={12} md="auto">
                    {successMessages[brand.id] && (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ color: '#4caf50', fontWeight: 'medium' }}
                      >
                        <CheckCircleIcon />
                        <Typography variant="body2">Sauvegardé</Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
      <Stack spacing={2} alignItems="center" style={{ marginTop: '20px' }}>
        <Pagination
          count={Math.ceil(totalItems / 10)}
          page={page}
          onChange={handleChange}
          color={themeColors.dark.content}
        />
      </Stack>
    </Container>
  );
};

export default SocialNetworkContainer;
